import React, { memo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styles from './ImgRecommend.module.less';
import SoftCardGameImg from '../../components/SoftCardGameImg/SoftCardGameImg';
import ASoftCardGameImg from '../../components/ASoftCardGameImg/ASoftCardGameImg';
import { sortSoftsByLocalSoftInit } from '../../utils/utils';
import { logging } from '../../actions/log';
import { useInView } from 'react-intersection-observer';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectLocalSoftsInit = createSelector(
  state => state.native,
  native => native.installedSoftInit,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const localSoftsInit = useSelector(selectLocalSoftsInit);

  const [allMenuData, setAllMenuData] = useState([]);
  const [showSofts, setShowSofts] = useState([]);
  const [activitIndex, setActivitIndex] = useState(0);
  const [activitSofts, setActivitSofts] = useState([]);
  const [activitSoftsPage, setActivitSoftsPage] = useState(0);

  const [pageSize, setPageSize] = useState(0);
  const LOG_ACTION_SOURCE = {
    c_menu: `${logInfo.tag}_Tab`,
    c_change: `${logInfo.tag}_Tab_Change`,
    source: `${logInfo.tag}_Tab`,
  };

  const [ref, inView] = useInView({
    threshold: 1,
  });

  useEffect(() => {
    if (inView && logInfo && logInfo.changView) {
      let { pageCardContentId } = data && data.dataList && data.dataList[0];
      if (data && data.dataList && data.dataList.length > 1) {
        pageCardContentId = data.dataList.reduce((total, item) => {
          return `${total}${total ? '||' : ''}${item.pageCardContentId}`;
        }, '');
      }
      logInfo.changView({
        pageCardId: pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
        cardType: data && data.cardType,
      });
    }
  }, [inView]);

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    const menuData = [];
    if (data && data.dataList && data.dataList.length > 0) {
      // 目前只支持行数配置，列数不支持配置，固定为3列。未配置则显示3个
      setPageSize(parseInt(data.row, 10) * 3 || 3);
      data.dataList.forEach(item => {
        const allSofts = sortSoftsByLocalSoftInit(item.apps || [], localSoftsInit);
        const menu = {
          code: item.code,
          name: item.name,
          pageCardContentId: item.pageCardContentId,
          allSofts,
        };
        if (allSofts && allSofts.length > 0) {
          menuData.push(menu);
        }
      });
      setAllMenuData(menuData);
    }
  }, [localSoftsInit]);

  useEffect(() => {
    if (allMenuData && allMenuData.length > 0) {
      handleChangeMenu(0, true);
    }
  }, [allMenuData]);

  /**
   * 每一次change 都是数组中循环展示6个  不够就用数组前面的应用组装成6个  这样依次循环展示
   * 根据保存的页码数，获取当前应该展示的6个应用
   */
  const getAppsByPage = useCallback(
    (softAll = [], page = 1) => {
      if (softAll && softAll.length === 0) {
        return [];
      }
      // const pageSize = 3;
      let softArrLoop = softAll;
      while (page * pageSize > softArrLoop.length) {
        softArrLoop = softArrLoop.concat(softAll);
      }
      return softArrLoop.slice((page - 1) * pageSize, page * pageSize);
    },
    [pageSize],
  );

  const handleChangeSoft = useCallback(
    (allSofts, page, flag) => {
      setActivitSoftsPage(page + 1);
      setShowSofts(getAppsByPage(allSofts, page + 1));

      // 点击换一换, flag第一次切换不上报
      if (!flag) {
        dispatch(
          logging({
            category: 'C',
            action: LOG_ACTION_SOURCE.c_change,
            label: '',
            param2: {
              source: `${LOG_ACTION_SOURCE.source}||${allMenuData[activitIndex].code}`,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
            },
          }),
        );
      }
    },
    [dispatch, allMenuData, activitIndex],
  );

  const handleChangeMenu = useCallback(
    (index, flag) => {
      if (allMenuData) {
        setActivitIndex(index);
        setActivitSofts(allMenuData[index].allSofts);
        // 设置换一换第1页
        setActivitSoftsPage(0);
        handleChangeSoft(allMenuData[index].allSofts, 0, true);
      }

      // 切换Tab, flag第一次切换不上报
      if (!flag) {
        dispatch(
          logging({
            category: 'C',
            action: LOG_ACTION_SOURCE.c_menu,
            label: '',
            param2: {
              source: `${LOG_ACTION_SOURCE.source}||${allMenuData[index].code}`,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
            },
          }),
        );
      }
    },
    [dispatch, allMenuData],
  );

  return allMenuData && allMenuData.length > 0 && showSofts && showSofts.length > 0 ? (
    <div className={styles.card} ref={ref}>
      <div className={styles.card_header}>
        {allMenuData.length > 1 ? (
          <div className={styles.header_menu}>
            {/* <div className={styles.menu_title}>精品推荐</div> */}
            {allMenuData.map((menu, i) => (
              <div
                className={activitIndex === i ? styles.menu_item_activit : styles.menu_item}
                onMouseOver={() => {
                  handleChangeMenu(i);
                }}
                onFocus={() => undefined}
                key={i}
                title={menu.name}
              >
                {menu.name}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.header_menu}>
            {allMenuData.map((menu, i) => (
              <div
                className={styles.menu_title}
                onMouseOver={() => {
                  handleChangeMenu(i);
                }}
                onFocus={() => undefined}
                key={i}
                title={menu.name}
              >
                {menu.name}
              </div>
            ))}
          </div>
        )}
        {allMenuData &&
          allMenuData[activitIndex] &&
          allMenuData[activitIndex].allSofts &&
          allMenuData[activitIndex].allSofts.length > pageSize && (
            <div
              className={styles.header_change}
              onClick={() => {
                handleChangeSoft(activitSofts, activitSoftsPage);
              }}
            >
              <i className={`iconfont icon-refresh1 ${styles.header_change_icon}`} />
              <span>换一换</span>
            </div>
          )}
      </div>
      <div className={styles.card_content}>
        {showSofts.map((item, j) =>
          item.androidType === '1' ? (
            <ASoftCardGameImg
              soft={item}
              logInfo={{
                ...logInfo,
                source: `${LOG_ACTION_SOURCE.source}||${allMenuData[activitIndex].code}`,
              }}
              key={j}
            />
          ) : (
            <SoftCardGameImg
              soft={item}
              logInfo={{
                ...logInfo,
                source: `${LOG_ACTION_SOURCE.source}||${allMenuData[activitIndex].code}`,
              }}
              key={j}
            />
          ),
        )}
      </div>
    </div>
  ) : (
    ''
  );
});
